/**
 * OIDC Authorization Code + PKCE helpers for the Keycloak login flow.
 *
 * The previous "Sign in with Keycloak" button redirected with
 * `response_type=code` but NO `code_challenge` (PKCE) and NO `state` (CSRF), and
 * nothing processed the returning `?code=`. A public SPA client MUST use PKCE
 * (RFC 7636) and a state nonce, then exchange the code for tokens at the token
 * endpoint. This module implements both legs.
 */

const VERIFIER_KEY = 'tyndo.oidc.pkce_verifier';
const STATE_KEY = 'tyndo.oidc.state';
const RETURN_KEY = 'tyndo.oidc.return_to';

export interface OidcConfig {
  authority: string; // e.g. http://localhost:8080/realms/tyndo
  clientId: string;
  redirectUri: string;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength: number): string {
  const arr = new Uint8Array(byteLength);
  crypto.getRandomValues(arr);
  return base64UrlEncode(arr);
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

/**
 * Resolve OIDC config from injected globals (set by the host) with dev
 * fallbacks. Mirrors the lookup the old LoginPage did inline.
 */
export function resolveOidcConfig(): OidcConfig {
  const g = (typeof globalThis !== 'undefined' ? globalThis : {}) as Record<string, unknown>;
  const keycloakUrl = (g.REACT_APP_KEYCLOAK_URL as string | undefined) || 'http://localhost:8080';
  const realm = (g.REACT_APP_KEYCLOAK_REALM as string | undefined) || 'tyndo';
  const clientId = (g.REACT_APP_KEYCLOAK_CLIENT_ID as string | undefined) || 'tyndo';
  return {
    authority: `${keycloakUrl}/realms/${encodeURIComponent(realm)}`,
    clientId,
    redirectUri: `${window.location.origin}/auth/callback`,
  };
}

/**
 * Begin the Authorization Code + PKCE flow: mint a verifier + challenge + state,
 * stash them in sessionStorage, and redirect the browser to the authorize
 * endpoint.
 */
export async function beginLogin(config: OidcConfig, returnTo = '/'): Promise<void> {
  const verifier = randomString(32);
  const state = randomString(16);
  const challenge = base64UrlEncode(await sha256(verifier));

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.href = `${config.authority}/protocol/openid-connect/auth?${params.toString()}`;
}

export interface TokenResult {
  accessToken: string;
  returnTo: string;
}

/**
 * Complete the flow: validate the returned `state`, then exchange the `code`
 * for tokens at the token endpoint using the stored PKCE verifier. Throws on
 * state mismatch (CSRF), a provider error, or a failed exchange.
 */
export async function completeLogin(config: OidcConfig, search: string): Promise<TokenResult> {
  const url = new URLSearchParams(search);
  const error = url.get('error');
  if (error) {
    throw new Error(url.get('error_description') || `Authorization failed: ${error}`);
  }

  const code = url.get('code');
  const returnedState = url.get('state');
  if (!code) throw new Error('Missing authorization code on callback');

  const expectedState = sessionStorage.getItem(STATE_KEY);
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('State mismatch — possible CSRF; aborting login');
  }

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier — restart the login flow');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  // audit-allow: direct-fetch -- auth bootstrap transport: the OIDC token
  // exchange runs against the IdP's authorization-server token endpoint
  // (config.authority), not the Tyndo API gateway, and happens before any
  // access token (and therefore the shared authenticated API client) exists.
  const res = await fetch(`${config.authority}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = data?.error_description || data?.error || detail;
    } catch {
      /* ignore */
    }
    throw new Error(`Token exchange failed: ${detail}`);
  }

  const data = (await res.json()) as { access_token?: string };
  if (!data.access_token) throw new Error('Token response missing access_token');

  const returnTo = sessionStorage.getItem(RETURN_KEY) || '/';
  // One-shot artifacts — clear so a replay can't reuse them.
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  return { accessToken: data.access_token, returnTo };
}
